import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { RadioCard, RadioCardContent, RadioCardDescription, RadioCardItem, RadioGroup } from "@/components/ui/radio-group";
import { useAuth } from "@clerk/react";
import NumberFlow from "@number-flow/react";
import {
  BellRingIcon,
  EyeIcon,
  GavelIcon,
  InfoIcon,
  MessageCircleQuestionIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  SparklesIcon,
  WrenchIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FrameDescription,
  FramePanel,
  FrameFooter,
} from "@/components/ui/frame";
import {
  SwitchCard,
  SwitchCardSwitch,
  SwitchCardContent,
  SwitchCardLabel,
  SwitchCardDescription,
} from "@/components/ui/switch-card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BadgeStatus } from "@/components/dashboard_campaigns/status-badges";
import { useCampaignStore } from "@/stores";
import { useEveActionsStore } from "@/stores/eve/eveActionsStore";
import { useFundingStore } from "@/stores/eve/fundingStore";
import { useAmyDeepLink } from "@/hooks/use-amy-deep-link";
import { EvePhasePatch, EvePipelinePhase } from "@/types/eve";
import { evePhaseLabel, resolveEvePhaseSettings } from "@/lib/eve";
import {
  entryStatusFor,
  phaseDescription,
  phaseHasJudging,
  phaseHasNudges,
  phaseIcon,
} from "./shared";
import { ContractTemplateEditor } from "./ContractTemplateEditor";

/**
 * Editor for ONE pipeline phase (mount with key={phase.key} so local state
 * resets on phase switch). Core settings save through the amy action route
 * (the SAME validated executor as the update_campaign_phases chat tool);
 * the contract template + prefill mapping has its own editor
 * (ContractTemplateEditor, same route); custom-step / event-hold config stays chat-only and renders
 * read-only with a deep link.
 */

type JudgeMode = "auto" | "approval" | "assist";


const JUDGE_MODES: { mode: JudgeMode; title: string; hint?: string; description: string; icon: React.ReactNode }[] = [
  {
    mode: "approval",
    title: "Team reviews",
    hint: "(Default)",
    description: "Every submission waits for your verdict on the Tasks page.",
    icon: <EyeIcon className="size-4 shrink-0" />,
  },
  {
    mode: "assist",
    title: "Amy assists",
    description:
      "Amy sends revision requests herself, but only your approve moves the creator forward.",
    icon: <GavelIcon className="size-4 shrink-0" />,
  },
  {
    mode: "auto",
    title: "Amy judges",
    description: "Amy reviews each submission and approves or requests revisions on her own.",
    icon: <SparklesIcon className="size-4 shrink-0" />,
  },
];

export function PhaseSection({
  phase,
  pipeline,
  targetTiers,
  scope,
}: {
  phase: EvePipelinePhase;
  /** The pipeline `phase` belongs to — the selected tier's array in the per-tier view. */
  pipeline: EvePipelinePhase[];
  /** Tiers every save here targets: the selected tier in the per-tier view, null = every tier. */
  targetTiers: number[] | null;
  /** Multi-tier campaigns: which tiers the edits here reach, under the title. */
  scope?: string;
}) {
  const { campaign, campaignId } = useCampaignStore();
  const { getToken, orgId } = useAuth();
  const { updateCampaignPhases, isSavingConfig } = useEveActionsStore();
  const fundingStore = useFundingStore();
  const openAmy = useAmyDeepLink();

  const label = evePhaseLabel(phase, pipeline);
  const Icon = phaseIcon(phase);
  const enabled = phase.enabled === true;
  const isCustom = phase.key.startsWith("x-");
  const isDraft = phase.key === "draft";
  const isEarlyPayout = phase.key === "payment_1" || phase.key === "payment_2";
  const isFinalPayment = phase.key === "payment";
  const server = resolveEvePhaseSettings(phase);
  const serverTextOnly = phase.settings?.text_only_approval ?? false;
  const serverInstructions = phase.instructions ?? "";
  const contractNeedsTemplate = phase.key === "contract" && phase.settings?.template_id == null;
  // Mirrors the server's payout enable gate (ANY slot — payment_1/payment_2/
  // payment): a platform-wallet org with no client funding on record can't
  // turn payouts on. Reads the store the sidebar's Client Funds row keeps
  // fresh — funding UI itself lives there.
  const paymentNeedsFunding =
    (isFinalPayment || isEarlyPayout) &&
    fundingStore.mode === "platform" &&
    (fundingStore.funding?.available ?? 0) <= 0;

  // Per-frame local state, re-seeded only when ITS server value changes —
  // saving one frame never clobbers unsaved edits in another.
  const [instructions, setInstructions] = useState(serverInstructions);
  useEffect(() => setInstructions(serverInstructions), [serverInstructions]);

  const [judgeMode, setJudgeMode] = useState<JudgeMode>(server.judgeMode);
  useEffect(() => setJudgeMode(server.judgeMode), [server.judgeMode]);
  const [handoffAfter, setHandoffAfter] = useState(server.handoffAfterRevisions);
  useEffect(() => setHandoffAfter(server.handoffAfterRevisions), [server.handoffAfterRevisions]);
  const [textOnly, setTextOnly] = useState(serverTextOnly);
  useEffect(() => setTextOnly(serverTextOnly), [serverTextOnly]);

  const [nudgeEnabled, setNudgeEnabled] = useState(server.nudgeEnabled);
  useEffect(() => setNudgeEnabled(server.nudgeEnabled), [server.nudgeEnabled]);
  const [maxNudges, setMaxNudges] = useState(server.maxNudges);
  useEffect(() => setMaxNudges(server.maxNudges), [server.maxNudges]);
  const [nudgeWaitSeconds, setNudgeWaitSeconds] = useState(server.nudgeWaitSeconds);
  useEffect(() => setNudgeWaitSeconds(server.nudgeWaitSeconds), [server.nudgeWaitSeconds]);

  const [percent, setPercent] = useState(server.percent);
  useEffect(() => setPercent(server.percent), [server.percent]);

  // Split-payout context: the enabled early sub-payments and their total
  // share — the final Payment's read-only "pays the remainder" note.
  const enabledEarlyPayouts = pipeline.filter(
    (p) => (p.key === "payment_1" || p.key === "payment_2") && p.enabled === true
  );
  const earlyPercentTotal = enabledEarlyPayouts.reduce(
    (sum, p) => sum + resolveEvePhaseSettings(p).percent,
    0
  );

  const instructionsDirty = instructions !== serverInstructions;
  const judgingDirty =
    judgeMode !== server.judgeMode ||
    handoffAfter !== server.handoffAfterRevisions ||
    (isDraft && textOnly !== serverTextOnly);
  const nudgesDirty =
    nudgeEnabled !== server.nudgeEnabled ||
    maxNudges !== server.maxNudges ||
    nudgeWaitSeconds !== server.nudgeWaitSeconds;
  const percentDirty = isEarlyPayout && percent !== server.percent;

  const save = async (patches: EvePhasePatch[]): Promise<boolean> => {
    const token = await getToken();
    if (!token || !campaignId || !orgId) {
      console.error("No token found");
      return false;
    }
    return updateCampaignPhases(campaignId, patches, orgId, token, { tiers: targetTiers });
  };

  const handleToggleEnabled = (value: boolean) => save([{ key: phase.key, enabled: value }]);

  const handleSaveInstructions = () => save([{ key: phase.key, instructions }]);

  const handleSaveJudging = () => {
    const settings: EvePhasePatch["settings"] = {};
    if (judgeMode !== server.judgeMode) settings.judge_mode = judgeMode;
    if (handoffAfter !== server.handoffAfterRevisions) settings.handoff_after_revisions = handoffAfter;
    if (isDraft && textOnly !== serverTextOnly) settings.text_only_approval = textOnly;
    if (Object.keys(settings).length === 0) return;
    save([{ key: phase.key, settings }]);
  };

  const handleSaveNudges = () => {
    const settings: EvePhasePatch["settings"] = {};
    if (nudgeEnabled !== server.nudgeEnabled) settings.nudge_enabled = nudgeEnabled;
    if (maxNudges !== server.maxNudges) settings.max_nudges = maxNudges;
    if (nudgeWaitSeconds !== server.nudgeWaitSeconds) settings.nudge_wait_seconds = nudgeWaitSeconds;
    if (Object.keys(settings).length === 0) return;
    save([{ key: phase.key, settings }]);
  };

  const handleSavePercent = () => {
    if (percent === server.percent) return;
    save([{ key: phase.key, settings: { percent } }]);
  };

  const nudgeWaitDays = nudgeWaitSeconds / 86400;

  const enableSwitch = (blocked: boolean) => (
    <SwitchCard disabled={isSavingConfig || blocked}>
      <SwitchCardSwitch
        id={`phase_enabled_${phase.key}`}
        checked={enabled}
        onCheckedChange={handleToggleEnabled}
        aria-describedby={`phase_enabled_${phase.key}_desc`}
      />
      <SwitchCardContent>
        <SwitchCardLabel htmlFor={`phase_enabled_${phase.key}`}>
          {!isSavingConfig && enabled && <PlayCircleIcon />}
          {!isSavingConfig && !enabled && <PauseCircleIcon />}
          {isSavingConfig && <Spinner />}
          {label} phase
          <span className="text-muted-foreground text-xs">
            {" "}
            - {enabled ? "on" : "off"}
          </span>
        </SwitchCardLabel>
        <SwitchCardDescription
          id={`phase_enabled_${phase.key}_desc`}
          className="pl-6 text-wrap"
        >
          Applies to creators entering the pipeline from now on.
        </SwitchCardDescription>
      </SwitchCardContent>
    </SwitchCard>
  );

  return (
    <div className="space-y-6">
      <Frame>
        <FrameHeader>
          <FrameTitle className="flex items-center gap-2">
            <Icon className="size-4 text-muted-foreground" />
            {label}
            <Tooltip delayDuration={300}>
              <TooltipTrigger className="rounded-md hover:bg-muted cursor-help p-1">
                <InfoIcon className="size-3.5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                {phaseDescription(phase)}
              </TooltipContent>
            </Tooltip>
            {isCustom && phase.shape && (
              <Badge variant="muted" size="sm" className="capitalize">
                {phase.shape} step
              </Badge>
            )}
            <span className="ml-auto">
              <BadgeStatus
                status={entryStatusFor(phase)}
                pipeline={pipeline}
                size="sm"
                showDescription={false}
              />
            </span>
          </FrameTitle>
          {scope && <FrameDescription>{scope}</FrameDescription>}
        </FrameHeader>
        {contractNeedsTemplate && !enabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>{enableSwitch(true)}</div>
            </TooltipTrigger>
            <TooltipContent side="top">
              A DocuSeal template must be set on the Contract template card
              below first (Uplodio staff) — enabling the contract phase
              without one is refused.
            </TooltipContent>
          </Tooltip>
        ) : paymentNeedsFunding && !enabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>{enableSwitch(true)}</div>
            </TooltipTrigger>
            <TooltipContent side="top">
              No client funding on record — record the client's deposit from
              the Client Funds card in the sidebar before enabling payouts.
            </TooltipContent>
          </Tooltip>
        ) : (
          enableSwitch(false)
        )}
      </Frame>

      <Frame>
        <FrameHeader>
          <FrameTitle>Instructions</FrameTitle>
          <FrameDescription>
            {phase.shape === "action"
              ? "What Amy does when she runs this step."
              : phase.shape === "wait"
                ? "Context Amy keeps in mind during this hold."
                : "What Amy asks for and pays attention to in this phase."}
          </FrameDescription>
        </FrameHeader>
        {/* Panel-styled textarea (StagesTab template idiom) — a Textarea
            inside a FramePanel would be a box inside a box. */}
        <Textarea
          variant="panel"
          className="min-h-32"
          maxLength={4000}
          placeholder="e.g. Scripts must include the promo code and a clear call to action…"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          disabled={isSavingConfig}
        />
        {instructionsDirty && (
          <FrameFooter className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="animate-in fade-in-0 slide-in-from-right-4 duration-200"
              disabled={isSavingConfig}
              onClick={() => setInstructions(serverInstructions)}
              aria-label="Discard instruction changes"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="animate-in fade-in-0 slide-in-from-right-2 duration-200"
              disabled={isSavingConfig}
              onClick={handleSaveInstructions}
            >
              Save Instructions
            </Button>
          </FrameFooter>
        )}
      </Frame>

      {isEarlyPayout && (
        <Frame>
          <FrameHeader>
            <FrameTitle>Split amount</FrameTitle>
            <FrameDescription>
              The share of each creator's agreed price this early payment pays.
              The final Payment phase always pays the exact remainder of what
              was actually paid — a canceled or refunded early payout folds
              back into it automatically.
            </FrameDescription>
          </FrameHeader>
          <FramePanel>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Percent of the agreed price</Label>
                <span className="text-sm font-medium tabular-nums">
                  <NumberFlow value={percent} />%
                </span>
              </div>
              <Slider
                value={[percent]}
                min={5}
                max={90}
                step={5}
                disabled={isSavingConfig}
                onValueChange={(v) => setPercent(v[0])}
              />
              <p className="text-xs text-muted-foreground">
                All early payments together may use at most 90% — the final
                Payment keeps at least 10%.
              </p>
            </div>
          </FramePanel>
          {percentDirty && (
            <FrameFooter className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="animate-in fade-in-0 slide-in-from-right-4 duration-200"
                disabled={isSavingConfig}
                onClick={() => setPercent(server.percent)}
                aria-label="Discard split changes"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="animate-in fade-in-0 slide-in-from-right-2 duration-200"
                disabled={isSavingConfig}
                onClick={handleSavePercent}
              >
                Save Split
              </Button>
            </FrameFooter>
          )}
        </Frame>
      )}

      {isFinalPayment && enabledEarlyPayouts.length > 0 && (
        <Frame>
          <FrameHeader>
            <FrameTitle>Split amount</FrameTitle>
            <FrameDescription>
              This final payment automatically pays the remainder — currently{" "}
              {Math.max(0, 100 - earlyPercentTotal)}% of each agreed price (the
              earlier sub-payment{enabledEarlyPayouts.length > 1 ? "s" : ""}{" "}
              cover{enabledEarlyPayouts.length > 1 ? "" : "s"} {earlyPercentTotal}%). If an early payout is canceled
              or refunded, its share folds back in here. There is nothing to
              configure on this side.
            </FrameDescription>
          </FrameHeader>
        </Frame>
      )}

      {phaseHasJudging(phase) && (
        <Frame>
          <FrameHeader>
            <FrameTitle>Judging</FrameTitle>
          </FrameHeader>
          <RadioGroup
            value={judgeMode}
            onValueChange={(v) => setJudgeMode(v as JudgeMode)}
            disabled={isSavingConfig}
            className="lg:grid-cols-3"
          >
            {JUDGE_MODES.map((m) => (
              <RadioCard key={m.mode} className="gap-3 bg-background hover:bg-muted">
                {m.icon}
                <RadioCardContent>
                  <Label htmlFor={`judge-${m.mode}`}>
                    {m.title}
                    {m.hint && <span className="text-xs text-muted-foreground">{m.hint}</span>}
                  </Label>
                  <RadioCardDescription>{m.description}</RadioCardDescription>
                </RadioCardContent>
                <RadioCardItem value={m.mode} id={`judge-${m.mode}`} />
              </RadioCard>
            ))}
          </RadioGroup>
          <FramePanel>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <Tooltip delayDuration={500}>
                    <TooltipTrigger className="flex flex-row items-center gap-2 hover:bg-muted p-1.5 rounded-sm cursor-help">
                      <Label>Hand-off After (revisions)</Label>
                      <InfoIcon className="size-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      After this many revision rounds, Amy pauses the creator
                      and hands the case to you
                    </TooltipContent>
                  </Tooltip>
                  <span className="font-mono text-sm font-medium">
                    <NumberFlow value={handoffAfter} />
                  </span>
                </div>
                <Slider
                  value={[handoffAfter]}
                  min={1}
                  max={20}
                  step={1}
                  onValueChange={(v) => setHandoffAfter(v[0])}
                />
              </div>
              {isDraft && (
                <SwitchCard disabled={isSavingConfig}>
                  <SwitchCardSwitch
                    id={`text_only_${phase.key}`}
                    checked={textOnly}
                    onCheckedChange={setTextOnly}
                  />
                  <SwitchCardContent>
                    <SwitchCardLabel htmlFor={`text_only_${phase.key}`}>
                      Allow text-only approval
                    </SwitchCardLabel>
                    <SwitchCardDescription className="text-wrap">
                      Let the judge approve from caption and text checks when no
                      submitted file could be watched.
                    </SwitchCardDescription>
                  </SwitchCardContent>
                </SwitchCard>
              )}
            </div>
          </FramePanel>
          {judgingDirty && (
            <FrameFooter className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="animate-in fade-in-0 slide-in-from-right-4 duration-200"
                disabled={isSavingConfig}
                onClick={() => {
                  setJudgeMode(server.judgeMode);
                  setHandoffAfter(server.handoffAfterRevisions);
                  setTextOnly(serverTextOnly);
                }}
                aria-label="Discard judging changes"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="animate-in fade-in-0 slide-in-from-right-2 duration-200"
                disabled={isSavingConfig}
                onClick={handleSaveJudging}
              >
                Save Judging
              </Button>
            </FrameFooter>
          )}
        </Frame>
      )}

      {phaseHasNudges(phase) && (
        <Frame>
          <FrameHeader>
            <FrameTitle>Nudges</FrameTitle>
          </FrameHeader>
          {/* Panel collapses to nothing while it's just the switch (StagesTab
              Completion idiom) — it becomes a real panel once the sliders
              expand alongside it. */}
          <FramePanel
            className={`${nudgeEnabled ? "p-4" : "p-0 border-none"} transition-all`}
          >
            <SwitchCard disabled={isSavingConfig}>
              <SwitchCardSwitch
                id={`nudges_${phase.key}`}
                checked={nudgeEnabled}
                onCheckedChange={setNudgeEnabled}
              />
              <SwitchCardContent>
                <SwitchCardLabel htmlFor={`nudges_${phase.key}`}>
                  <BellRingIcon className="size-4" />
                  Nudges Enabled
                  <span className="text-muted-foreground text-xs">
                    {" "}
                    - Auto-remind unresponsive creators
                  </span>
                </SwitchCardLabel>
                <SwitchCardDescription className="pl-6 text-wrap">Even on approval-mode campaigns.</SwitchCardDescription>
              </SwitchCardContent>
            </SwitchCard>

            {nudgeEnabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 ml-3 pl-3 pb-4 border-l border-dashed animate-in fade-in-0 slide-in-from-top-2 duration-150">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <Tooltip delayDuration={500}>
                      <TooltipTrigger className="flex flex-row items-center gap-2 hover:bg-muted p-1.5 rounded-sm cursor-help">
                        <Label>Wait Time (days)</Label>
                        <InfoIcon className="size-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        Days to wait before sending a nudge
                      </TooltipContent>
                    </Tooltip>
                    <span className="font-mono text-sm font-medium">
                      <NumberFlow value={Math.round(nudgeWaitDays)} />
                    </span>
                  </div>
                  <Slider
                    value={[Math.round(nudgeWaitDays)]}
                    min={1}
                    max={14}
                    step={1}
                    onValueChange={([val]) => setNudgeWaitSeconds(val * 86400)}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <Tooltip delayDuration={500}>
                      <TooltipTrigger className="flex flex-row items-center gap-2 hover:bg-muted p-1.5 rounded-sm cursor-help">
                        <Label>Max Nudges</Label>
                        <InfoIcon className="size-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        Maximum number of nudges before Amy stops
                      </TooltipContent>
                    </Tooltip>
                    <span className="font-mono text-sm font-medium">
                      <NumberFlow value={maxNudges} />
                    </span>
                  </div>
                  <Slider
                    value={[maxNudges]}
                    min={0}
                    max={10}
                    step={1}
                    onValueChange={([val]) => setMaxNudges(val)}
                  />
                </div>
              </div>
            )}
          </FramePanel>
          {nudgesDirty && (
            <FrameFooter className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="animate-in fade-in-0 slide-in-from-right-4 duration-200"
                disabled={isSavingConfig}
                onClick={() => {
                  setNudgeEnabled(server.nudgeEnabled);
                  setMaxNudges(server.maxNudges);
                  setNudgeWaitSeconds(server.nudgeWaitSeconds);
                }}
                aria-label="Discard nudge changes"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="animate-in fade-in-0 slide-in-from-right-2 duration-200"
                disabled={isSavingConfig}
                onClick={handleSaveNudges}
              >
                Save Nudges
              </Button>
            </FrameFooter>
          )}
        </Frame>
      )}

      {phase.key === "contract" && (
        <ContractTemplateEditor phase={phase} save={save} disabled={isSavingConfig} />
      )}

      {isCustom && phase.shape !== "wait" && (
        <Frame>
          <FrameHeader>
            <FrameTitle>Tools</FrameTitle>
          </FrameHeader>
          <FramePanel className="flex flex-col gap-2 text-sm">
            {phase.tool_slugs && phase.tool_slugs.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1">
                <WrenchIcon className="size-3.5 text-muted-foreground" />
                {phase.tool_slugs.map((slug) => (
                  <Badge key={slug} variant="muted" size="sm">
                    {slug}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-muted-foreground">No tools configured.</span>
            )}
            <Button
              variant="secondary"
              size="sm"
              className="w-fit self-end"
              onClick={() =>
                openAmy(
                  `Update the "${label}" custom phase (tools, outputs, label) of campaign "${campaign?.campaign_name ?? ""}" (campaign_id ${campaignId}).`
                )
              }
            >
              <MessageCircleQuestionIcon /> Edit with Amy
            </Button>
          </FramePanel>
        </Frame>
      )}

      {isCustom && phase.shape === "review" && (
        <Frame>
          <FrameHeader>
            <FrameTitle>Timing</FrameTitle>
          </FrameHeader>
          <FramePanel className="flex flex-col gap-2 text-sm">
            <span>
              {phase.timer
                ? `First email ${phase.timer.offset_days} day${phase.timer.offset_days === 1 ? "" : "s"} ${phase.timer.before ? "before" : "after"} the creator's "${phase.timer.field}" date. Replies before then are handled as usual.`
                : "First email goes out as soon as the creator reaches this step."}
            </span>
            <Button
              variant="secondary"
              size="sm"
              className="w-fit self-end"
              onClick={() =>
                openAmy(
                  `Update the "${label}" step's timed start (date field / days before or after) of campaign "${campaign?.campaign_name ?? ""}" (campaign_id ${campaignId}).`
                )
              }
            >
              <MessageCircleQuestionIcon /> Edit with Amy
            </Button>
          </FramePanel>
        </Frame>
      )}

      {phase.shape === "wait" && (
        <Frame>
          <FrameHeader>
            <FrameTitle>Release</FrameTitle>
          </FrameHeader>
          <FramePanel className="flex flex-col gap-2 text-sm">
            <span>
              {phase.manual
                ? "Released manually by the team."
                : phase.trigger?.slug
                  ? `Released by ${phase.trigger.slug}${phase.trigger.match?.length
                    ? ` (${phase.trigger.match.length} match condition${phase.trigger.match.length === 1 ? "" : "s"})`
                    : ""
                  }.`
                  : "Event-released."}
            </span>
            {phase.wait_message && (
              <p className="italic text-muted-foreground">"{phase.wait_message}"</p>
            )}
            <Button
              variant="secondary"
              size="sm"
              className="w-fit self-end"
              onClick={() =>
                openAmy(
                  `Update the "${label}" hold (release trigger / wait message) of campaign "${campaign?.campaign_name ?? ""}" (campaign_id ${campaignId}).`
                )
              }
            >
              <MessageCircleQuestionIcon /> Edit with Amy
            </Button>
          </FramePanel>
        </Frame>
      )}
    </div>
  );
}
